import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import {
  parseJwt,
  isTokenExpired,
  refreshAccessToken,
  getAuthUrl,
  getLogoutUrl,
} from '../utils/oauth';

// Storage keys
const ACCESS_TOKEN_KEY = 'auth_access_token';
const ID_TOKEN_KEY = 'auth_id_token';
const REFRESH_TOKEN_KEY = 'auth_refresh_token';
const LOGIN_IN_PROGRESS_KEY = 'auth_login_in_progress'; // Flag to prevent restoring old tokens during login
const LOGIN_FAILED_KEY = 'auth_login_failed'; // Flag to prevent restoring tokens after a failed login

export interface User {
  sub: string;
  email: string;
  email_verified: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  [key: string]: any;
}

export interface AuthTokens {
  accessToken: string | null;
  idToken: string | null;
  refreshToken: string | null;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isDataSyncing: boolean;
  tokens: AuthTokens;
  login: (redirectPath?: string | null) => Promise<void>;
  logout: () => void;
  refreshToken: (refreshToken: string) => Promise<string>;
  getAccessToken: () => Promise<string | null>;
  setAuthTokens: (accessToken: string, idToken: string, refreshToken?: string) => void;
  setDataSyncing: (syncing: boolean) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): React.JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDataSyncing, setIsDataSyncing] = useState<boolean>(false);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);

  const tokens: AuthTokens = {
    idToken,
    accessToken,
    refreshToken,
  }

  const clearAuthState = (): void => {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(ID_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setAccessToken(null);
    setIdToken(null);
    setRefreshToken(null);
    setUser(null);
    setIsAuthenticated(false);
  };

  const saveTokens = (accessToken: string, idToken: string, refreshToken: string | null): void => {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(ID_TOKEN_KEY, idToken);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
    
    const userInfo = parseJwt(idToken) as User;
    setAccessToken(accessToken);
    setIdToken(idToken);
    setRefreshToken(refreshToken);
    setUser(userInfo);
    setIsAuthenticated(true);
  };

  const refreshTokenAsync = useCallback(async (refreshToken: string): Promise<string> => {
    try {
      const response = await refreshAccessToken(refreshToken);
      
      // Refresh token response may not include refresh_token
      // If it does, use it; otherwise keep the existing one
      const newRefreshToken = response.refresh_token || refreshToken;
      
      saveTokens(
        response.access_token,
        response.id_token,
        newRefreshToken
      );
      
      return response.access_token;
    } catch (error) {
      console.error('Token refresh error:', error);
      clearAuthState();
      throw error;
    }
  }, []);

  const login = useCallback(async (redirectPath: string | null = null): Promise<void> => {
    try {
      sessionStorage.setItem(LOGIN_IN_PROGRESS_KEY, 'true');
      
      // Clear any existing auth state before starting a new login
      // This prevents old tokens from making the user appear logged in if the new login fails
      clearAuthState();
      
      // Set flag to indicate login is in progress
      // This prevents AuthContext from restoring old tokens during the login flow
      
      const authUrl = await getAuthUrl(redirectPath);
      window.location.href = authUrl;
    } catch (error) {
      console.error('Login error:', error);
      // Clear the login in progress flag and auth state on error
      sessionStorage.removeItem(LOGIN_IN_PROGRESS_KEY);
      clearAuthState();
      throw error;
    }
  }, []);

  const logout = useCallback((): void => {
    // Clear local auth state first
    clearAuthState();
    
    // Clear any login-related flags
    sessionStorage.removeItem(LOGIN_IN_PROGRESS_KEY);
    sessionStorage.removeItem(LOGIN_FAILED_KEY);
    
    // Redirect to Cognito logout URL for global sign-out
    // This will:
    // 1. Sign the user out of Cognito globally (invalidates all tokens)
    // 2. Clear Cognito session cookies
    // 3. Redirect back to /logout route
    // 
    // Note: The logout_uri must be in the allowed logout URLs in Cognito
    // (configured in Terraform as web_logout_urls)
    try {
      const logoutUrl = getLogoutUrl();
      window.location.href = logoutUrl;
    } catch (error) {
      console.error('Error generating logout URL:', error);
      // If logout URL generation fails, just redirect to home
      // The local state is already cleared
      window.location.href = '/';
    }
  }, []);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    if (!accessToken) {
      return null;
    }

    // Check if token needs refresh
    if (isTokenExpired(accessToken)) {
      if (refreshToken && !isTokenExpired(refreshToken)) {
        try {
          const newAccessToken = await refreshTokenAsync(refreshToken);
          return newAccessToken;
        } catch (error) {
          return null;
        }
      } else {
        return null;
      }
    }

    return accessToken;
  }, [accessToken, refreshToken, refreshTokenAsync]);

  const setAuthTokens = useCallback((accessToken: string, idToken: string, refreshToken?: string): void => {
    // Clear the login in progress and failed flags when tokens are successfully set
    sessionStorage.removeItem(LOGIN_IN_PROGRESS_KEY);
    sessionStorage.removeItem(LOGIN_FAILED_KEY);
    // Mark that data sync will begin (DataSyncOnAuth will set this false when done)
    setIsDataSyncing(true);
    saveTokens(accessToken, idToken, refreshToken || null);
  }, []);

  const setDataSyncing = useCallback((syncing: boolean): void => {
    setIsDataSyncing(syncing);
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    isDataSyncing,
    tokens,
    login,
    logout,
    refreshToken: refreshTokenAsync,
    getAccessToken,
    setAuthTokens,
    setDataSyncing,
  };

  // Load auth state from localStorage on mount
  useEffect(() => {
    const loadAuthState = async (): Promise<void> => {
      try {
        // Check if a login is in progress - if so, don't restore old tokens
        const loginInProgress = sessionStorage.getItem(LOGIN_IN_PROGRESS_KEY);
        const loginFailed = sessionStorage.getItem(LOGIN_FAILED_KEY);

        if (loginInProgress) {
          setIsLoading(true);
          return;
        }

        if (loginFailed) {
          clearAuthState();
          sessionStorage.removeItem(LOGIN_FAILED_KEY);
          setIsLoading(false);
          return;
        }

        const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
        const idToken = localStorage.getItem(ID_TOKEN_KEY);
        const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

        if (accessToken && idToken) {
          if (isTokenExpired(accessToken)) {
            // Try refresh before giving up - await so we don't flash "session expired"
            if (refreshToken && !isTokenExpired(refreshToken)) {
              try {
                await refreshTokenAsync(refreshToken);
                return; // refreshTokenAsync calls saveTokens; loading will be cleared below
              } catch {
                console.log('Token refresh failed, clearing auth state');
                clearAuthState();
              }
            } else {
              clearAuthState();
            }
          } else {
            const userInfo = parseJwt(idToken) as User;
            setAccessToken(accessToken);
            setIdToken(idToken);
            setRefreshToken(refreshToken);
            setUser(userInfo);
            setIsAuthenticated(true);
          }
        }
      } catch (error) {
        console.error('Error loading auth state:', error);
        clearAuthState();
      } finally {
        setIsLoading(false);
      }
    };

    loadAuthState();
  }, [refreshTokenAsync]);

  // Auto-refresh token before expiration; clear auth when refresh fails so user can re-login
  useEffect(() => {
    const checkAndRefresh = async (): Promise<void> => {
      if (!tokens.accessToken || !isTokenExpired(tokens.accessToken)) return;
      if (tokens.refreshToken) {
        try {
          await refreshTokenAsync(tokens.refreshToken);
        } catch (error) {
          console.error('Auto-refresh failed:', error);
          clearAuthState();
        }
      } else {
        clearAuthState();
      }
    };

    // Check immediately
    checkAndRefresh();

    // Check every 5 minutes
    const interval = setInterval(checkAndRefresh, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [
    tokens.accessToken,
    tokens.refreshToken,
    refreshTokenAsync,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
